import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccessTime,
  DarkMode,
  Dashboard as DashboardIcon,
  LightMode,
  School,
  VolumeOff,
  VolumeUp,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import { Html5Qrcode } from 'html5-qrcode';
import { buildStudentQrPayload } from '../utils/studentQr';
import { API_URL } from '../utils/api';

const SCANNER_ID = 'qr-kiosk-scanner';
const COOLDOWN_MS = 6000;
const FAIL_SOUND_COOLDOWN_MS = 2500;

/** Beep pendek seperti scanner barcode: "tit" */
function playScanTick(ctx, { freq = 2400, durationMs = 0.04, volume = 0.55 } = {}) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'square';
  const t0 = ctx.currentTime;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.linearRampToValueAtTime(volume, t0 + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + durationMs);
  osc.start(t0);
  osc.stop(t0 + durationMs + 0.015);
}

function playScanDoubleTit(ctx) {
  playScanTick(ctx, { freq: 2600, durationMs: 0.042, volume: 0.6 });
  setTimeout(() => playScanTick(ctx, { freq: 2200, durationMs: 0.04, volume: 0.52 }), 55);
}

function playScanErrorTit(ctx) {
  playScanTick(ctx, { freq: 420, durationMs: 0.14, volume: 0.65 });
}

function formatScannerError(err) {
  if (!err) return 'Kamera tidak bisa diakses';
  if (typeof err === 'string') return err;
  return err.message || err.name || 'Kamera tidak bisa diakses';
}

function formatTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatClockWib(date) {
  return date.toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatDateHeader() {
  return new Date().toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).toUpperCase();
}

/** Status pengiriman WA ke orang tua setelah scan QR */
function parentWaHint(pn) {
  if (!pn) return '';
  if (pn.sent === true) return ' Notifikasi WA ke orang tua terkirim.';
  switch (pn.reason) {
    case 'no_parent_phone':
      return ' WA tidak terkirim: nomor orang tua belum diisi di data siswa.';
    case 'wa_not_connected':
      return ' WA tidak terkirim: WhatsApp sekolah belum terhubung (scan QR di Pengaturan).';
    case 'checkout_disabled':
      return ' WA pulang tidak aktif (nyalakan di Pengaturan).';
    case 'teacher_no_wa':
      return '';
    case 'duplicate_skipped':
      return ' WA tidak dikirim ulang (sudah pernah untuk absensi ini).';
    case 'no_token':
      return ' WA tidak terkirim: provider Fonnte tanpa token.';
    case 'fonnte_rejected':
      return ' WA ditolak Fonnte (cek token/kuota).';
    case 'network_error':
      return ' WA gagal: error jaringan server.';
    default:
      return ' Notifikasi WA tidak terkirim.';
  }
}

/** Sudut L putih seperti demo kiosk */
function ScanFrameOverlay() {
  const corner = {
    position: 'absolute',
    width: 48,
    height: 48,
    borderColor: '#fff',
    borderStyle: 'solid',
  };
  return (
    <Box
      sx={{
        pointerEvents: 'none',
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ position: 'relative', width: 280, height: 280 }}>
        <Box sx={{ ...corner, top: 0, left: 0, borderWidth: '4px 0 0 4px' }} />
        <Box sx={{ ...corner, top: 0, right: 0, borderWidth: '4px 4px 0 0' }} />
        <Box sx={{ ...corner, bottom: 0, left: 0, borderWidth: '0 0 4px 4px' }} />
        <Box sx={{ ...corner, bottom: 0, right: 0, borderWidth: '0 4px 4px 0' }} />
      </Box>
    </Box>
  );
}

const headerIconBtnSx = {
  bgcolor: 'rgba(255,255,255,0.22)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.25)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.38)' },
};

const QrKiosk = () => {
  const { isAuthenticated } = useAuth();
  const { mode, toggleTheme, colors } = useThemeContext();
  const isDark = mode === 'dark';
  const accent = isDark ? '#60a5fa' : '#2563eb';
  const scannerRef = useRef(null);
  const handleScanRef = useRef(null);
  const initLockRef = useRef(false);
  const lastScanRef = useRef({ key: '', at: 0 });
  const processingRef = useRef(false);
  const audioContextRef = useRef(null);
  const lastFailSoundAtRef = useRef(0);

  const [clock, setClock] = useState(() => new Date());
  const [statusMsg, setStatusMsg] = useState('Menyiapkan kamera...');
  const [statusType, setStatusType] = useState('info');
  const [recent, setRecent] = useState([]);
  const [scannerReady, setScannerReady] = useState(false);
  const [schoolName, setSchoolName] = useState('Absensi SD');
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [manualNis, setManualNis] = useState('');
  const [soundOn, setSoundOn] = useState(true);

  const playBeep = useCallback((type, force = false) => {
    if (!force && !soundOn) return;
    const now = Date.now();
    if (type === 'fail' && now - lastFailSoundAtRef.current < FAIL_SOUND_COOLDOWN_MS) return;

    try {
      if (!audioContextRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new Ctx();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      if (type === 'success') {
        playScanDoubleTit(ctx);
      } else {
        playScanErrorTit(ctx);
        lastFailSoundAtRef.current = now;
      }
    } catch (_) {
      // ignore
    }
  }, [soundOn]);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/attendance/kiosk-recent`, {
        params: { limit: 5 },
      });
      if (data.success) setRecent(data.data || []);
    } catch (_) {
      // ignore
    }
  }, []);

  const loadSchoolInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/attendance/kiosk-info`);
      if (data.success && data.data?.school_name) {
        setSchoolName(data.data.school_name);
      }
    } catch (_) {
      // ignore
    }
  }, []);

  const handleScan = useCallback(async (decodedText) => {
    const text = String(decodedText || '').trim();
    if (!text || processingRef.current) return;

    const now = Date.now();
    if (lastScanRef.current.key === text && now - lastScanRef.current.at < COOLDOWN_MS) {
      return;
    }
    lastScanRef.current = { key: text, at: now };
    processingRef.current = true;

    try {
      const { data: json } = await axios.post(`${API_URL}/api/attendance/qr-scan`, {
        qr_text: text,
        location: 'Gerbang Sekolah - Kiosk QR',
      });

      if (json.success) {
        const d = json.data || {};
        const pn = d.parent_notification;
        const eventLabel = d.event_label || (json.event === 'checkout' ? 'PULANG' : 'MASUK');
        const waLine = parentWaHint(pn);
        setStatusMsg(`${eventLabel}: ${d.employee_name}.${waLine}`);
        setStatusType(json.event === 'checkout' ? 'checkout' : 'checkin');
        playBeep('success');
        await loadRecent();
      } else {
        setStatusMsg(json.message || json.error || 'Scan gagal');
        setStatusType('warning');
        playBeep('fail');
      }
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.error
        || formatScannerError(err);
      setStatusMsg(msg);
      setStatusType(err.response?.status === 429 ? 'warning' : 'error');
      playBeep('fail');
    } finally {
      processingRef.current = false;
    }
  }, [loadRecent, playBeep]);

  handleScanRef.current = handleScan;

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      if (s.isScanning) await s.stop();
      await s.clear();
    } catch (_) {
      // ignore
    }
    scannerRef.current = null;
    setScannerReady(false);
  }, []);

  const startScanner = useCallback(async (cameraIdOrConfig) => {
    await stopScanner();
    const el = document.getElementById(SCANNER_ID);
    if (!el) {
      setStatusMsg('Scanner belum siap. Muat ulang halaman.');
      setStatusType('error');
      return;
    }

    const html5Qr = new Html5Qrcode(SCANNER_ID, { verbose: false });
    const scanConfig = {
      fps: 12,
      qrbox: (vw, vh) => {
        const size = Math.min(vw, vh) * 0.72;
        return { width: Math.floor(size), height: Math.floor(size) };
      },
    };
    const onDecode = (decoded) => handleScanRef.current?.(decoded);

    if (typeof cameraIdOrConfig === 'string' && cameraIdOrConfig) {
      await html5Qr.start(cameraIdOrConfig, scanConfig, onDecode, () => {});
    } else if (cameraIdOrConfig && typeof cameraIdOrConfig === 'object') {
      await html5Qr.start(cameraIdOrConfig, scanConfig, onDecode, () => {});
    } else {
      await html5Qr.start({ facingMode: 'user' }, scanConfig, onDecode, () => {});
    }

    scannerRef.current = html5Qr;
    setScannerReady(true);
    setStatusMsg('Tempelkan kartu QR ke area scan');
    setStatusType('info');
  }, [stopScanner]);

  const bootstrapScanner = useCallback(async () => {
    if (initLockRef.current) return;
    initLockRef.current = true;

    try {
      setStatusMsg('Meminta izin kamera...');
      let deviceList = [];
      try {
        deviceList = await Html5Qrcode.getCameras();
      } catch (camErr) {
        console.warn('[QR Kiosk] getCameras:', camErr);
      }
      setCameras(deviceList);

      if (deviceList.length > 0) {
        const preferred =
          deviceList.find((c) => /usb|webcam|integrated/i.test(c.label || ''))
          || deviceList.find((c) => /back|rear|environment/i.test(c.label || ''))
          || deviceList[0];
        setSelectedCameraId(preferred.id);
        await startScanner(preferred.id);
        return;
      }

      setSelectedCameraId('facing:user');
      await startScanner({ facingMode: 'user' });
    } catch (err) {
      try {
        await startScanner({ facingMode: 'environment' });
      } catch (err2) {
        console.error('[QR Kiosk]', err2 || err);
        setStatusMsg(
          'Kamera tidak aktif. Izinkan kamera di browser, atau ketik NIS di bawah.'
        );
        setStatusType('warning');
      }
    } finally {
      initLockRef.current = false;
    }
  }, [startScanner]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    loadSchoolInfo();
    loadRecent();
    const poll = setInterval(loadRecent, 12000);
    return () => clearInterval(poll);
  }, [loadSchoolInfo, loadRecent]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await new Promise((r) => requestAnimationFrame(r));
      if (!cancelled) await bootstrapScanner();
    })();
    return () => {
      cancelled = true;
      initLockRef.current = false;
      stopScanner();
    };
  }, [bootstrapScanner, stopScanner]);

  const handleCameraChange = async (event) => {
    const value = event.target.value;
    setSelectedCameraId(value);
    try {
      if (value.startsWith('facing:')) {
        await startScanner({ facingMode: value.replace('facing:', '') });
      } else {
        await startScanner(value);
      }
    } catch (err) {
      setStatusMsg(`Gagal ganti kamera: ${formatScannerError(err)}`);
      setStatusType('error');
      playBeep('fail');
    }
  };

  const submitManualNis = () => {
    const nis = manualNis.trim();
    if (!nis) return;
    handleScan(buildStudentQrPayload(nis));
    setManualNis('');
  };

  const alertSeverity =
    statusType === 'checkin'
      ? 'success'
      : statusType === 'checkout'
        ? 'info'
        : statusType === 'warning'
          ? 'warning'
          : statusType === 'error'
            ? 'error'
            : 'info';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.background, color: colors.textPrimary }}>
      {/* Header mirip demo */}
      <Box
        sx={{
          background: 'linear-gradient(90deg, #312e81 0%, #4338ca 50%, #312e81 100%)',
          color: '#fff',
          px: { xs: 2, md: 3 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          boxShadow: '0 2px 12px rgba(49,46,129,0.35)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 44, height: 44 }}>
            <School />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {schoolName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Kiosk Digital
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontFamily: 'monospace', letterSpacing: 0.5, mr: 1 }}
          >
            {formatClockWib(clock)}
            <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.85 }}>
              WIB
            </Typography>
          </Typography>

          <Tooltip title={soundOn ? 'Matikan suara scan' : 'Nyalakan suara scan (tit)'}>
            <IconButton
              onClick={() => {
                const next = !soundOn;
                setSoundOn(next);
                if (next) playBeep('success', true);
              }}
              sx={{
                bgcolor: soundOn ? '#facc15' : 'rgba(255,255,255,0.15)',
                color: soundOn ? '#1e293b' : '#fff',
                border: soundOn ? 'none' : '1px solid rgba(255,255,255,0.25)',
                '&:hover': { bgcolor: soundOn ? '#eab308' : 'rgba(255,255,255,0.28)' },
              }}
            >
              {soundOn ? <VolumeUp /> : <VolumeOff />}
            </IconButton>
          </Tooltip>

          <Tooltip title={isDark ? 'Mode terang' : 'Mode malam'}>
            <IconButton onClick={toggleTheme} sx={headerIconBtnSx} aria-label="Toggle dark mode">
              {isDark ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              isAuthenticated
                ? 'Ke dashboard admin'
                : 'Login dulu untuk membuka dashboard'
            }
          >
            <span>
              <IconButton
                component={RouterLink}
                to={isAuthenticated ? '/dashboard' : '/login'}
                state={isAuthenticated ? undefined : { from: '/kiosk-qr' }}
                sx={headerIconBtnSx}
              >
                <DashboardIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Typography
        align="center"
        sx={{
          py: 2,
          fontWeight: 800,
          letterSpacing: 1.2,
          color: accent,
          fontSize: { xs: '0.95rem', md: '1.1rem' },
        }}
      >
        {formatDateHeader()}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.35fr 1fr' },
          gap: 2.5,
          px: { xs: 2, md: 3 },
          pb: 3,
          maxWidth: 1280,
          mx: 'auto',
        }}
      >
        {/* SCAN AREA */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${colors.border}`,
            bgcolor: colors.card,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: accent }}>
              SCAN AREA
            </Typography>
            {cameras.length > 0 && (
              <Select
                size="small"
                value={selectedCameraId}
                onChange={handleCameraChange}
                sx={{ minWidth: 200, maxWidth: 280, fontSize: '0.8rem' }}
              >
                {cameras.map((cam) => (
                  <MenuItem key={cam.id} value={cam.id} dense>
                    {cam.label || cam.id}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          <Box
            sx={{
              position: 'relative',
              mx: 2,
              mb: 1,
              bgcolor: '#111827',
              borderRadius: 2,
              minHeight: 380,
              overflow: 'hidden',
            }}
          >
            <div id={SCANNER_ID} style={{ width: '100%', minHeight: 380 }} />
            {scannerReady && <ScanFrameOverlay />}
          </Box>

          {statusMsg && (
            <Alert severity={alertSeverity} sx={{ mx: 2, mb: 1 }} variant="outlined">
              {statusMsg}
            </Alert>
          )}

          {!scannerReady && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Button size="small" variant="outlined" onClick={bootstrapScanner}>
                Aktifkan kamera
              </Button>
            </Box>
          )}

          <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Ketik NIS / NIP jika QR tidak terbaca"
              value={manualNis}
              onChange={(e) => setManualNis(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && submitManualNis()}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <Button variant="contained" size="small" onClick={submitManualNis} disabled={!manualNis.trim()}>
              Absen
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1.5, display: 'block' }}>
            Scan 1 = masuk · Scan 2 (hari sama) = pulang
          </Typography>
        </Paper>

        {/* 5 ABSEN TERAKHIR */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${colors.border}`,
            bgcolor: colors.card,
            p: 2,
            minHeight: 420,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccessTime sx={{ color: accent, fontSize: 22 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: accent }}>
              5 ABSEN TERAKHIR
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          {recent.length === 0 ? (
            <Typography color="text.secondary" variant="body2" sx={{ py: 4, textAlign: 'center' }}>
              Belum ada absensi hari ini
            </Typography>
          ) : (
            recent.map((row) => {
              const isPulang = row.event_label === 'PULANG';
              return (
                <Box
                  key={`${row.id}-${row.time}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.75,
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  <Avatar sx={{ width: 44, height: 44, bgcolor: isDark ? '#475569' : '#cbd5e1' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: colors.textPrimary }} noWrap>
                      {row.employee_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {row.class_label}
                    </Typography>
                  </Box>
                  <Chip
                    label={row.event_label || 'MASUK'}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: isPulang ? '#dbeafe' : '#dcfce7',
                      color: isPulang ? '#1d4ed8' : '#15803d',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: '#64748b', minWidth: 44, textAlign: 'right' }}
                  >
                    {formatTime(row.time)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Paper>
      </Box>

      <Box sx={{ textAlign: 'center', pb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7, display: 'block' }}>
          Absensi SD — Kiosk QR
        </Typography>
        {/* <Link component={RouterLink} to="/login" variant="caption" sx={{ mt: 0.5 }}>
          Login administrator
        </Link> */}
      </Box>
    </Box>
  );
};

export default QrKiosk;
